/**
 * Wishlist engine — localStorage-based, no app required.
 *
 * Storage shape (localStorage key WISHLIST_KEY):
 * [
 *   {
 *     productId, variantId, handle, title, image, price, quantity: 1
 *   },
 *   ...
 * ]
 *
 * Fires `wishlist:updated` on `document` (CustomEvent<{ items }>) whenever
 * the list changes, so any number of buttons/icons on the page can stay in sync.
 */

const WISHLIST_KEY = 'theme:wishlist';
const UPDATED_EVENT = 'wishlist:updated';

export const WishlistStore = {
  getItems() {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },

  saveItems(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch {
      // localStorage unavailable (private mode / quota) — fail silently
    }
    document.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail: { items } }));
  },

  has(variantId) {
    return this.getItems().some((item) => String(item.variantId) === String(variantId));
  },

  add(item) {
    const items = this.getItems();
    if (!items.some((i) => String(i.variantId) === String(item.variantId))) {
      items.push({ quantity: 1, ...item });
      this.saveItems(items);
    }
    return items;
  },

  remove(variantId) {
    const items = this.getItems().filter((item) => String(item.variantId) !== String(variantId));
    this.saveItems(items);
    return items;
  },

  toggle(item) {
    return this.has(item.variantId) ? this.remove(item.variantId) : this.add(item);
  },

  count() {
    return this.getItems().length;
  },
};

/**
 * <wishlist-icon> — lives in the header. Shows a count bubble, kept in sync
 * with the store. Clicking is handled by the surrounding button's
 * `on:click="#wishlist-drawer/toggle"` attribute (same pattern as the cart icon).
 */
class WishlistIcon extends HTMLElement {
  connectedCallback() {
    this.bubble = this.querySelector('[data-wishlist-count]');
    this.#render();
    this._onUpdate = () => this.#render();
    document.addEventListener(UPDATED_EVENT, this._onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(UPDATED_EVENT, this._onUpdate);
  }

  #render() {
    const count = WishlistStore.count();
    if (!this.bubble) return;
    this.bubble.textContent = String(count);
    this.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);
    this.bubble.closest('.wishlist-bubble')?.classList.toggle('visually-hidden', count === 0);
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

/**
 * <wishlist-button> — drop this on any product card / product page.
 * Required data attributes:
 *   data-product-id, data-variant-id, data-handle, data-title, data-image, data-price
 *
 * Usage (see snippets/wishlist-button.liquid):
 *   <wishlist-button
 *     data-product-id="{{ product.id }}"
 *     data-variant-id="{{ product.selected_or_first_available_variant.id }}"
 *     data-handle="{{ product.handle }}"
 *     data-title="{{ product.title | escape }}"
 *     data-image="{{ product.featured_image | image_url: width: 400 }}"
 *     data-price="{{ product.selected_or_first_available_variant.price }}"
 *   >
 *     <button type="button" class="wishlist-button__toggle" aria-pressed="false">
 *       <span class="svg-wrapper">{{ 'icon-wishlist.svg' | inline_asset_content }}</span>
 *     </button>
 *   </wishlist-button>
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#handleClick);
    this.#syncState();
    this._onUpdate = () => this.#syncState();
    document.addEventListener(UPDATED_EVENT, this._onUpdate);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
    document.removeEventListener(UPDATED_EVENT, this._onUpdate);
  }

  #handleClick = (event) => {
    event.preventDefault();
    const item = {
      productId: this.dataset.productId,
      variantId: this.dataset.variantId,
      handle: this.dataset.handle,
      title: this.dataset.title,
      image: this.dataset.image,
      price: this.dataset.price,
    };
    WishlistStore.toggle(item);
  };

  #syncState() {
    const active = WishlistStore.has(this.dataset.variantId);
    this.classList.toggle('wishlist-button--active', active);
    if (this.button) {
      this.button.setAttribute('aria-pressed', String(active));
      this.button.setAttribute(
        'aria-label',
        active ? 'Remove from wishlist' : 'Add to wishlist'
      );
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * <wishlist-drawer-component> — renders the list of saved items inside
 * snippets/wishlist-drawer.liquid and handles remove / add-to-cart actions.
 */
class WishlistDrawerComponent extends HTMLElement {
  connectedCallback() {
    this.itemsContainer = this.querySelector('[data-wishlist-items]');
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    this.#render();
    this._onUpdate = () => this.#render();
    document.addEventListener(UPDATED_EVENT, this._onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(UPDATED_EVENT, this._onUpdate);
  }

  #render() {
    const items = WishlistStore.getItems();

    if (!this.itemsContainer) return;

    if (items.length === 0) {
      this.itemsContainer.innerHTML = '';
      this.emptyState?.removeAttribute('hidden');
      return;
    }

    this.emptyState?.setAttribute('hidden', '');

    this.itemsContainer.innerHTML = items
      .map(
        (item) => `
        <div class="wishlist-drawer__item" data-variant-id="${item.variantId}">
          <a href="/products/${item.handle}" class="wishlist-drawer__item-image">
            ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy" width="80" height="80">` : ''}
          </a>
          <div class="wishlist-drawer__item-details">
            <a href="/products/${item.handle}" class="wishlist-drawer__item-title">${item.title}</a>
            <span class="wishlist-drawer__item-price">${this.#formatMoney(item.price)}</span>
            <div class="wishlist-drawer__item-actions">
              <button type="button" class="button button-primary wishlist-drawer__add-to-cart" data-variant-id="${item.variantId}">
                Add to cart
              </button>
              <button type="button" class="button-unstyled wishlist-drawer__remove" data-variant-id="${item.variantId}">
                Remove
              </button>
            </div>
          </div>
        </div>
      `
      )
      .join('');

    this.itemsContainer.querySelectorAll('.wishlist-drawer__remove').forEach((btn) => {
      btn.addEventListener('click', () => WishlistStore.remove(btn.dataset.variantId));
    });

    this.itemsContainer.querySelectorAll('.wishlist-drawer__add-to-cart').forEach((btn) => {
      btn.addEventListener('click', () => this.#addToCart(btn));
    });
  }

  #formatMoney(cents) {
    // Basic fallback formatter. Replace with `Shopify.formatMoney` if you
    // have the money_format script loaded, for correct currency formatting.
    const amount = (Number(cents) / 100).toFixed(2);
    return `$${amount}`;
  }

  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!response.ok) throw new Error('Add to cart failed');

      // Refresh the header section so the cart bubble/count updates.
      // Relies on the theme's section-hydration module already used in header.liquid.
      const headerSection = document.querySelector('.shopify-section[id*="header"]');
      if (headerSection) {
        const { hydrate } = await import(window.Shopify?.theme ? '@theme/section-hydration' : '@theme/section-hydration');
        const sectionId = headerSection.id.replace('shopify-section-', '');
        hydrate(sectionId, new URL(window.location.href));
      }

      // Open the cart drawer if present, then close the wishlist drawer.
      document.querySelector('#cart-drawer')?.dispatchEvent(new CustomEvent('theme:drawer:open'));
      this.closest('theme-drawer')?.removeAttribute('open');

      button.textContent = 'Added ✓';
      setTimeout(() => {
        button.textContent = 'Add to cart';
        button.disabled = false;
      }, 1500);
    } catch (err) {
      console.error('Wishlist add-to-cart error:', err);
      button.textContent = 'Try again';
      button.disabled = false;
    }
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
